/**
 * useArTableActions Hook
 *
 * Provides CRUD operations for ArTable rows.
 * Handles add, delete, clear, duplicate, lock/unlock operations.
 *
 * Three kinds of edit reach `formUndoStack`, each through its own entry, because
 * what an entry has to carry differs by what the edit destroyed.
 *
 * The *value* edits record `'fields'` entries (SYD-2297): `pasteDownColumn`,
 * `updateAnnotation` and `updateBundleValue`, one `record` call per user action.
 * Absence is not expressible on either side of such an entry: no plain write can
 * express a `deleteField()` sentinel as `next`, and `null` is not a stand-in for a
 * `prev` the field never had. So a clear is never recorded, nor is a first-time set
 * of a field whose readers test `!== undefined` — see `updateAnnotation` and
 * `clearRow`.
 *
 * `pasteRows` records through `recordDocuments` (SYD-2314), one entry naming every
 * row the paste landed, because a document's existence — unlike a field's — is
 * expressible with nothing on one side.
 *
 * `deleteRows` is the one destructive action undo reaches (SYD-2447), and it
 * reaches it as an `'operation'` entry: the server files a trash record and hands
 * back an `operationId`, so nothing about the destroyed rows has to travel through
 * the stack. `stepRowOperation` is the other half — it reverses one by id and is
 * the only writer of the pagination counters on that path, which is why the
 * `ArTableBody` adapter delegates here instead of calling the service itself.
 *
 * Every other structural operation — add, duplicate, lock, reorder and every
 * bundle membership change — stays out of undo's scope.
 */

import { useContext, useCallback, useMemo, useRef } from 'react';
import { serverTimestamp, deleteField, getDocs, query, where } from 'firebase/firestore';
import { LexoRank } from 'lexorank';
import get from 'lodash/get';
import { MessageContext } from '@/providers/MessageContext';
import { useStoreNonReactive } from '@/store';
import { formUndoStack } from '@/store/form/formUndoStack';
import type { UndoChange, UndoDirection, UndoOperation } from '@/store/form/formUndoStack';
import { classifyArBundleRows } from '../classifyArBundleRows';
import { isColumnPasteable } from '../clipboard';
import type { PastedRow } from '../clipboard';
import { bundlePositionOf } from '../resolveArRowDrop';
import { allocateRowOrders } from './allocateRowOrders';
import { allocateMoveToBundleOrder } from './allocateMoveToBundleOrder';
import { AR_TABLE_AGGREGATIONS_ROW_ID, tableIdFromCollectionName } from '#types/System.Artifacts.types';
import type { ArRowData, ArRowId, ArTableRowScope, ArTableType, ArCell, ArCellValue, TypeId, ArBundleType, ArBundleValue } from '#types/System.Artifacts.types';
import type { CollectionPersist, CollectionState, DocumentState } from '@/store';
import type { ArTableUIState } from '../state/createArTableState';

type BulkActionResult = {
  affected: number;
  skipped: number;
  skippedIds: ArRowId[];
  /**
   * The call never landed and every count below is a fabrication. Callers must
   * emit no toast of their own and must not clear the selection: the action has
   * already reported the reason through `handleFirestoreWriteError`, and the
   * user needs the ticks to retry (SYD-2386).
   */
  failed: boolean;
};

/**
 * `derived` is a subset of `skipped`, not a separate outcome: the cells a Form
 * Derive projection owns were refused for a reason the user can act on, and
 * `pasteSummary` says so in the copy deck's words instead of the generic count.
 */
type PasteResult = BulkActionResult & { created: number; derived: number };

/**
 * Deliberately NOT a `BulkActionResult`: `affected`, `skipped` and `skippedIds`
 * are cell-paste vocabulary. A row paste lands exactly the rows it created, and
 * the cells it dropped were dropped by `resolveRowPaste` before this action saw
 * the grid — folding the two into one `skipped` counted cells as rows (SYD-2314).
 */
type PasteRowsResult = {
  created: number;
  /** Adds Firestore refused. `persist.add` mints ids only on success, so these have no ids. */
  rejected: number;
  /** The ids the adds minted, in paste order, for the undo entry a later step records. */
  newRowIds: ArRowId[];
  /** False when the anchor is not among `renderedRowIds`, which is a different outcome from a refused write. */
  anchorOnScreen: boolean;
  failed: boolean;
};

/**
 * A callable rejection the server reached deliberately — an exceeded bound, an
 * unrepresentable scope, a partially-committed all-rows batch that names how far
 * it got. Retrying changes nothing, or changes what the message already said, so
 * it is shown verbatim and the generic "please try again" wording is kept for
 * transport failures (SYD-2306).
 */
const isDeterministicRejection = (error: unknown): boolean => {
  const code = (error as { code?: unknown }).code;
  return code === 'functions/invalid-argument'
    || code === 'functions/failed-precondition'
    || code === 'functions/aborted';
};

/**
 * What the undo buttons call a delete they can reverse, so `Undo ${label}` and
 * `Redo ${label}` both read as English at either count.
 */
const deletedRowsLabel = (count: number): string =>
  `${count} deleted ${count === 1 ? 'row' : 'rows'}`;

/**
 * What a half-committed run says about itself in its `aborted` rejection's
 * `details`. Both callables raise it through the same `commitInBatches`, which
 * stamps `affectedCount` with the write functions that landed — rows destroyed by a
 * delete or a redo, rows restored by an undo — so both paths read it here rather
 * than each parsing the payload their own way.
 *
 * `details` is a callable payload and nothing guarantees its shape, so a missing or
 * malformed one yields `null`: the counter must move by what the server named or
 * not at all, and a second error raised inside a catch block would replace the
 * failure the user needs to read.
 */
const abortedRunReceipt = (error: unknown): { committedCount: number; operationId: string | null } | null => {
  if (typeof error !== 'object' || error === null) return null;
  const details = (error as { details?: unknown }).details;
  if (typeof details !== 'object' || details === null) return null;
  const { operationId, affectedCount } = details as {
    operationId?: unknown;
    affectedCount?: unknown;
  };
  if (typeof affectedCount !== 'number' || !Number.isFinite(affectedCount)) return null;
  if (affectedCount <= 0) return null;
  return {
    committedCount: affectedCount,
    operationId: typeof operationId === 'string' && operationId.length > 0 ? operationId : null,
  };
};

/**
 * The undo entry a half-committed delete earns. Those batches already destroyed
 * rows, so the entry has to be recorded from the rejection or the rows the run did
 * destroy are stranded with nothing naming them. Without the `operationId` the trash
 * filed them under there is nothing to address, so no entry is offered.
 */
const abortedDeleteReceipt = (error: unknown): UndoOperation | null => {
  const receipt = abortedRunReceipt(error);
  if (receipt === null || receipt.operationId === null) return null;
  return {
    operationId: receipt.operationId,
    count: receipt.committedCount,
    label: deletedRowsLabel(receipt.committedCount),
  };
};

/**
 * The rows a failed bulk call left untouched. Neither `allRows` nor `bundleId` can
 * name them: both scopes are enumerated server-side.
 */
const scopeFallback = (scope: ArTableRowScope): BulkActionResult => ({
  affected: 0,
  skipped: scope.rowIds?.length ?? 0,
  skippedIds: (scope.rowIds ?? []) as ArRowId[],
  failed: true,
});

type Actions = {
  addRow: (
    initialCells?: Record<TypeId, ArCell>,
    options?: { afterRowId?: ArRowId; bundleId?: string; order?: string }
  ) => Promise<string | null>;
  clearRow: (rowId: ArRowId) => Promise<void>;
  duplicateRow: (rowId: ArRowId) => Promise<string | null>;
  lockRow: (rowId: ArRowId, locked: boolean) => Promise<void>;
  moveRowInManualOrder: (nextRowIds: ArRowId[], rowId: ArRowId) => Promise<void>;
  updateAnnotation: (rowId: ArRowId, annotation: string | undefined) => Promise<void>;
  getRowLocked: (rowId: ArRowId) => boolean;
  getRowAnnotation: (rowId: ArRowId) => string | undefined;
  lastAddedRowId: React.RefObject<string | null>;
  /**
   * Moves the page counters by `delta` rows. Exposed for surfaces that create or
   * destroy rows outside these actions — the `formUndoStack` adapter reversing a
   * row paste is the one — so the counters keep a single writer.
   */
  bumpPaginationCount: (delta: number) => void;
  /** Bulk operations */
  pasteDownColumn: (
    anchorRowId: ArRowId,
    columnId: TypeId,
    values: ArCellValue[],
    options?: { overflow?: 'createRows' | 'stop' }
  ) => Promise<PasteResult>;
  pasteRows: (anchorRowId: ArRowId, grid: PastedRow[]) => Promise<PasteRowsResult>;
  lockRows: (scope: ArTableRowScope, locked: boolean) => Promise<BulkActionResult>;
  deleteRows: (scope: ArTableRowScope) => Promise<BulkActionResult>;
  /**
   * Moves one recorded delete to the direction's target state and reports what the
   * server found, `targetStateHolds` and `found` included and unaltered — only the
   * server knows whether a row was refused, or whether the trash still holds the
   * operation at all. `ArTableBody` hands this to `formUndoStack` as the rows
   * target's `stepOperation`, so the pagination counters keep a single writer.
   */
  stepRowOperation: (
    operationId: string,
    direction: UndoDirection,
  ) => Promise<{ applied: number; skipped: number; found: number; targetStateHolds: boolean }>;
  /** No scope: an all-rows duplicate is refused server-side, so it is unrepresentable here. */
  duplicateRows: (rowIds: ArRowId[]) => Promise<BulkActionResult>;
  /** Bundle operations */
  createBundle: (selectedRowIds: ArRowId[], bundleType: ArBundleType, bundleValue?: ArBundleValue) => Promise<void>;
  dissolveBundle: (headerRowId: ArRowId) => Promise<BulkActionResult>;
  removeFromBundle: (rowId: ArRowId) => Promise<void>;
  moveToBundle: (rowId: ArRowId, targetBundleId: string, afterRowId?: ArRowId) => Promise<void>;
  addMemberRow: (bundleId: string, afterRowId?: ArRowId) => Promise<string | null>;
  duplicateBundle: (headerRowId: ArRowId) => Promise<BulkActionResult>;
  deleteBundle: (headerRowId: ArRowId) => Promise<BulkActionResult>;
  moveBundleInManualOrder: (nextRowIds: ArRowId[], headerRowId: ArRowId) => Promise<void>;
  updateBundleValue: (headerRowId: ArRowId, bundleValue: ArBundleValue) => Promise<void>;
};

/**
 * Hook for row operations in ArTable.
 * Call from ArTableBody after stores are loaded.
 */
function useArTableActions(uiState: ArTableUIState): Actions {
  const { handleFirestoreWriteError, modal } = useContext(MessageContext);
  const { state: schemaStateRaw } = useStoreNonReactive('schema');
  const { state: rowsStateRaw, persist: rowsPersistRaw } = useStoreNonReactive('rows');

  const schemaState = schemaStateRaw as DocumentState<ArTableType>;
  const rowsState = rowsStateRaw as CollectionState<ArRowData>;
  const persist = rowsPersistRaw as CollectionPersist<ArRowData>;

  const lastAddedRowId = useRef<string | null>(null);

  const bumpPaginationCount = useCallback((delta: number) => {
    const p = uiState.pagination;
    p.totalRows = Math.max(0, p.totalRows + delta);
    p.totalPages = Math.ceil(p.totalRows / p.pageSize) || 1;
    if (p.currentPage > p.totalPages) p.currentPage = p.totalPages;
  }, [uiState]);

  if (persist.persistType !== 'collection') {
    throw new Error('[useArTableActions] Expected collection persist for rows');
  }

  const rowsDocs = rowsState.docs;

  /** The coordinates `arTableRowOperations` addresses, split off the rows collection path. */
  const tableCoordinates = useMemo(() => {
    const segments = persist.collectionRef.path.split('/');
    const collectionName = segments.pop()!;
    return {
      parentPath: segments.join('/'),
      tableId: tableIdFromCollectionName(collectionName) ?? collectionName,
    };
  }, [persist.collectionRef.path]);

  /**
   * Adds a new row and returns its ID.
   */
  const addRow = useCallback(async (
    initialCells?: Record<TypeId, ArCell>,
    options?: { afterRowId?: ArRowId; bundleId?: string; order?: string }
  ): Promise<string | null> => {
    try {
      const afterRowId = options?.afterRowId;

      const maxExistingOrder = Array.from(rowsDocs.values())
        .map((entry) => entry.data?.order)
        .filter((order): order is string => typeof order === 'string' && order.length > 0)
        .reduce((acc, order) => (acc === null || order > acc ? order : acc), null as string | null);

      const nextOrder = options?.order ?? (() => {
        if (!afterRowId) {
          return allocateRowOrders({ floorOrder: maxExistingOrder, ceilingOrder: null, count: 1 })[0];
        }

        const afterRowEntry = rowsState.docs.get(afterRowId);

        if (!afterRowEntry?.data) {
          handleFirestoreWriteError(
            new Error(`Row ${afterRowId} not found`),
            '[useArTableActions] add row',
            { userMessage: 'Failed to add row. Row not found.' }
          );
          return null;
        }

        const orderedRowIds = rowsState.docIds
          .filter((id) => id !== AR_TABLE_AGGREGATIONS_ROW_ID) as ArRowId[];
        const sourceIndex = orderedRowIds.indexOf(afterRowId);
        const nextRowId = sourceIndex >= 0 ? orderedRowIds[sourceIndex + 1] ?? null : null;

        const sourceOrder = afterRowEntry.data.order;
        const nextNeighborOrder = nextRowId
          ? rowsState.docs.get(nextRowId)?.data?.order
          : undefined;

        const floorOrder = typeof sourceOrder === 'string' && sourceOrder.length > 0
          ? sourceOrder
          : maxExistingOrder;
        const ceilingOrder = typeof nextNeighborOrder === 'string' && nextNeighborOrder.length > 0
          ? nextNeighborOrder
          : null;

        return allocateRowOrders({ floorOrder, ceilingOrder, count: 1 })[0];
      })();

      if (nextOrder === null) {
        return null;
      }

      const newRowId = await persist.add({
        cells: initialCells ?? {},
        createdAt: serverTimestamp(),
        modifiedAt: serverTimestamp(),
        order: nextOrder,
        ...(options?.bundleId ? { bundleId: options.bundleId, bundleRole: 'member' as const } : null),
      });

      lastAddedRowId.current = newRowId;
      bumpPaginationCount(1);
      return newRowId;
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] add row', {
        userMessage: 'Failed to add row. Please try again.',
      });
      return null;
    }
  }, [handleFirestoreWriteError, persist, rowsDocs, rowsState, bumpPaginationCount]);

  /**
   * Clears all cell values in a row (preserves structure).
   *
   * Deliberately records nothing on `formUndoStack`. The action also deletes the
   * row's annotation, and an absent field is not expressible as an undo `next` —
   * `null` is not a substitute, because every reader tests `annotation !==
   * undefined` (`ArRow`, `ArBundleHeaderRow`, `ArRowContextMenu.hasAnnotation`)
   * and would render an empty annotation strip forever. Recording only the cells
   * would offer a ⌘Z that silently drops the note, so no ⌘Z is offered at all.
   */
  const clearRow = useCallback(async (rowId: ArRowId): Promise<void> => {
    const columns = schemaState.data?.columns ?? [];
    const updates: Record<string, unknown> = {
      annotation: deleteField(),
    };

    for (const col of columns) {
      updates[`cells.${col.typeId}.value`] = null;
    }

    try {
      await persist.update(rowId, updates, { debounce: false });
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] clear row', {
        userMessage: 'Failed to clear row. Please try again.',
      });
    }
  }, [persist, schemaState, handleFirestoreWriteError]);

  /**
   * Duplicates a row and returns the new row ID.
   *
   * A member's copy keeps `bundleId` and `bundleRole`, so it lands inside the
   * bundle it was copied from rather than as an ungrouped row sitting in the
   * bundle's order span — a state whose header would keep printing the old member
   * count (SYD-2306). A header is refused outright: copying a bundle means copying
   * its members too, which only `duplicateBundle` can do.
   *
   * Placement goes through `allocateRowOrders`, the single source of row ordering,
   * so a future ordering fix cannot miss this path.
   */
  const duplicateRow = useCallback(async (rowId: ArRowId): Promise<string | null> => {
    const rowEntry = rowsState.docs.get(rowId);

    if (!rowEntry?.data) {
      handleFirestoreWriteError(
        new Error(`Row ${rowId} not found`),
        '[useArTableActions] duplicate row',
        { userMessage: 'Failed to duplicate row. Row not found.' }
      );
      return null;
    }

    const originalData = rowEntry.data;

    if (originalData.bundleRole === 'header') {
      handleFirestoreWriteError(
        new Error(`Row ${rowId} is a bundle header`),
        '[useArTableActions] duplicate row',
        { userMessage: 'Use Duplicate on the group header to copy a whole bundle.' }
      );
      return null;
    }

    const duplicatedCells: Record<TypeId, ArCell> = {};
    for (const [typeId, cell] of Object.entries(originalData.cells || {})) {
      const cellData = cell as ArCell;
      duplicatedCells[typeId] = {
        value: cellData.value,
        marginalia: cellData.marginalia ? [...cellData.marginalia] : undefined,
        locked: false,
      };
    }

    try {
      const orderedRowIds = rowsState.docIds
        .filter((id) => id !== AR_TABLE_AGGREGATIONS_ROW_ID) as ArRowId[];
      const sourceIndex = orderedRowIds.indexOf(rowId);
      const nextRowId = sourceIndex >= 0 ? orderedRowIds[sourceIndex + 1] ?? null : null;

      const sourceOrder = originalData.order;
      const nextNeighborOrder = nextRowId
        ? rowsState.docs.get(nextRowId)?.data?.order
        : undefined;

      const maxExistingOrder = Array.from(rowsState.docs.values())
        .map((entry) => entry.data?.order)
        .filter((order): order is string => typeof order === 'string' && order.length > 0)
        .reduce((acc, order) => (acc === null || order > acc ? order : acc), null as string | null);

      const [nextOrder] = allocateRowOrders({
        floorOrder: (typeof sourceOrder === 'string' && sourceOrder.length > 0 ? sourceOrder : null)
          ?? maxExistingOrder,
        ceilingOrder: typeof nextNeighborOrder === 'string' && nextNeighborOrder.length > 0
          ? nextNeighborOrder
          : null,
        count: 1,
      });

      const newRowId = await persist.add({
        cells: duplicatedCells,
        locked: false,
        annotation: originalData.annotation,
        createdAt: serverTimestamp(),
        modifiedAt: serverTimestamp(),
        order: nextOrder,
        ...(originalData.bundleRole === 'member' && originalData.bundleId
          ? { bundleId: originalData.bundleId, bundleRole: 'member' as const }
          : null),
      });

      lastAddedRowId.current = newRowId;
      bumpPaginationCount(1);
      return newRowId;
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] duplicate row', {
        userMessage: 'Failed to duplicate row. Please try again.',
      });
      return null;
    }
  }, [rowsState, persist, handleFirestoreWriteError, bumpPaginationCount]);

  /**
   * Locks or unlocks a single row from the row menu, writing the flag straight
   * through `persist.update`.
   *
   * Deliberately NOT the bulk path (SYD-2386): a one-row toggle should not pay a
   * callable round trip, and it has no skip semantics to report — where the
   * callable leaves a row already in the target state alone and counts it as
   * skipped, this rewrites it, which is the same end state for a toggle nobody
   * counts. The two paths are one operation split by cost, not a leftover second
   * writer.
   */
  const lockRow = useCallback(async (rowId: ArRowId, locked: boolean): Promise<void> => {
    try {
      const mutableRow = rowsState.docs.get(rowId);
      if (mutableRow?.data) {
        mutableRow.data.locked = locked;
      }
      await persist.update(rowId, { locked }, { debounce: false });
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] lock row', {
        userMessage: `Failed to ${locked ? 'lock' : 'unlock'} row. Please try again.`,
      });
    }
  }, [persist, handleFirestoreWriteError, rowsState.docs]);

  /**
   * Updates or clears a row's annotation. Only a set over an annotation that
   * already exists is recorded, so both ends of the entry are real strings.
   * Clearing writes a `deleteField()` sentinel, which has no `next`; and the
   * first-time set ("Add annotation" writes `''`) has no `prev` either — reverting
   * it would have to restore an absent field, and `null` is not a substitute
   * because every reader tests `annotation !== undefined` (`ArRow`,
   * `ArBundleHeaderRow.showAnnotation`, `ArRowActions.hasAnnotation`,
   * `ArRowContextMenu.hasAnnotation`) and would render an empty strip forever with
   * "Remove annotation" as the only exit. Neither end is offered a ⌘Z.
   */
  const updateAnnotation = useCallback(async (rowId: ArRowId, annotation: string | undefined): Promise<void> => {
    try {
      const priorAnnotation = rowsState.docs.get(rowId)?.data?.annotation;
      if (annotation !== undefined && priorAnnotation !== undefined) {
        formUndoStack.record(persist.collectionRef.path, [{
          docId: rowId,
          path: 'annotation',
          prev: priorAnnotation,
          next: annotation,
        }]);
      }
      const value = annotation === undefined ? deleteField() : annotation;
      await persist.update(rowId, { annotation: value }, { debounce: false });
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] update annotation', {
        userMessage: 'Failed to update annotation. Please try again.',
      });
    }
  }, [persist, rowsState.docs, handleFirestoreWriteError]);

  /**
   * Gets the locked status of a row.
   */
  const getRowLocked = useCallback((rowId: ArRowId): boolean => {
    const rowEntry = rowsState.docs.get(rowId);
    return rowEntry?.data?.locked ?? false;
  }, [rowsState]);

  /**
   * Gets the annotation of a row.
   */
  const getRowAnnotation = useCallback((rowId: ArRowId): string | undefined => {
    const rowEntry = rowsState.docs.get(rowId);
    return rowEntry?.data?.annotation;
  }, [rowsState]);

  /**
   * Pastes `values` down a single column starting at `anchorRowId` (positional
   * alignment against `uiState.renderedRowIds`). `undefined` entries are
   * uncoercible clipboard input — skipped in place, existing cell values kept.
   * Locked rows/cells and bundle header rows are also dropped in place (never
   * shifted). Values past the last rendered row create new rows at the table
   * end (`overflow: 'createRows'`, the default) using precomputed contiguous
   * LexoRanks — `persist.add` is not optimistic, so overflow rows must not
   * chain via `afterRowId`.
   */
  const pasteDownColumn = useCallback(async (
    anchorRowId: ArRowId,
    columnId: TypeId,
    values: ArCellValue[],
    options?: { overflow?: 'createRows' | 'stop' }
  ): Promise<PasteResult> => {
    const overflow = options?.overflow ?? 'createRows';
    let affected = 0;
    let skipped = 0;
    let created = 0;
    let derived = 0;
    const skippedIds: ArRowId[] = [];

    const rendered = [...uiState.renderedRowIds] as ArRowId[];
    const anchorIndex = rendered.indexOf(anchorRowId);
    if (anchorIndex === -1) {
      return { affected: 0, skipped: values.length, skippedIds: [], created: 0, derived: 0, failed: false };
    }

    if (!isColumnPasteable(schemaState.data, columnId)) {
      return { affected: 0, skipped: values.length, skippedIds: [], created: 0, derived: 0, failed: false };
    }

    try {
      const writes: Promise<void>[] = [];
      const overflowValues: ArCellValue[] = [];
      /** One entry for the whole paste, so a single ⌘Z reverts every row it touched. */
      const changes: UndoChange[] = [];

      for (let i = 0; i < values.length; i++) {
        const value = values[i];
        if (value === undefined) {
          skipped++;
          continue;
        }

        const targetRowId = rendered[anchorIndex + i];

        if (targetRowId === undefined) {
          if (overflow === 'stop') {
            skipped++;
          } else {
            overflowValues.push(value);
          }
          continue;
        }

        const mutableRow = rowsState.docs.get(targetRowId);
        if (!mutableRow?.data) {
          skipped++;
          skippedIds.push(targetRowId);
          continue;
        }

        const rowLocked = mutableRow.data.locked === true;
        const cellLocked = mutableRow.data.cells?.[columnId]?.locked === true;
        /**
         * The Form Derive reconciler owns this cell. Writing it would be undone
         * on the next pass, so the paste skips it and counts it separately —
         * the report names the reason instead of burying it in `skipped`.
         */
        const cellCalculated = mutableRow.data.cells?.[columnId]?._calculated === true;
        const isBundleHeader = mutableRow.data.bundleRole === 'header';
        if (rowLocked || cellLocked || cellCalculated || isBundleHeader) {
          skipped++;
          if (cellCalculated) derived++;
          skippedIds.push(targetRowId);
          continue;
        }

        const path = `cells.${columnId}.value`;
        changes.push({
          docId: targetRowId,
          path,
          prev: get(mutableRow.data, path) ?? null,
          next: value,
        });

        if (!mutableRow.data.cells) {
          mutableRow.data.cells = {};
        }
        if (!mutableRow.data.cells[columnId]) {
          mutableRow.data.cells[columnId] = { value: null };
        }
        mutableRow.data.cells[columnId].value = value;

        affected++;
        const rowIdForWrite = targetRowId;
        writes.push(
          persist.update(rowIdForWrite, { [`cells.${columnId}.value`]: value }, { debounce: false }).catch(() => {
            skipped++;
            skippedIds.push(rowIdForWrite);
            affected--;
          })
        );
      }

      formUndoStack.record(persist.collectionRef.path, changes);

      await Promise.allSettled(writes);

      if (overflow === 'createRows' && overflowValues.length > 0) {
        const maxExistingOrder = Array.from(rowsState.docs.values())
          .map((entry) => entry.data?.order)
          .filter((order): order is string => typeof order === 'string' && order.length > 0)
          .reduce((acc, order) => (acc === null || order > acc ? order : acc), null as string | null);

        const seed = maxExistingOrder ?? LexoRank.middle().toString();
        const ranks = allocateRowOrders({ floorOrder: seed, ceilingOrder: null, count: overflowValues.length });

        for (let j = 0; j < overflowValues.length; j++) {
          const initialCells: Record<TypeId, ArCell> = { [columnId]: { value: overflowValues[j] } };
          const newId = await addRow(initialCells, { order: ranks[j] });
          if (newId) {
            created++;
          } else {
            skipped++;
          }
        }
      }

      return { affected, skipped, skippedIds, created, derived, failed: false };
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] paste down column', {
        userMessage: 'Failed to paste. Please try again.',
      });
      return { affected, skipped, skippedIds, created, derived, failed: true };
    }
  }, [addRow, handleFirestoreWriteError, persist, rowsState.docs, schemaState.data, uiState]);

  /**
   * Inserts `grid` as new rows under `anchorRowId`, the focused row — a row paste
   * inserts, it never overwrites what sits below the anchor.
   *
   * The anchor decides both ends of the paste: position, and membership through
   * the same `bundlePositionOf` a row drag-and-drop resolves against — onto a
   * header or a member every new row joins that bundle, onto an ungrouped row
   * they carry no bundle fields at all. The ranks are one precomputed contiguous
   * block between the anchor and its next neighbour, so the adds go out in
   * parallel; a rejected one is counted in `rejected` and named by no id, since
   * `persist.add` mints ids only on success.
   *
   * An anchor missing from `renderedRowIds` returns `anchorOnScreen: false` and
   * writes nothing — a distinct outcome from a refused write, and the caller
   * tells the user to scroll rather than reporting rows it never attempted.
   *
   * The landed rows are recorded as ONE `'documents'` entry, so a single ⌘Z takes
   * all of them away and ⌘⇧Z brings them back under the same ids. Recording waits
   * for the adds because the ids come from them.
   */
  const pasteRows = useCallback(async (
    anchorRowId: ArRowId,
    grid: PastedRow[]
  ): Promise<PasteRowsResult> => {
    const zeroResult: PasteRowsResult = {
      created: 0,
      rejected: 0,
      newRowIds: [],
      anchorOnScreen: true,
      failed: false,
    };

    if (grid.length === 0) return zeroResult;

    const rendered = [...uiState.renderedRowIds] as ArRowId[];
    if (!rendered.includes(anchorRowId)) return { ...zeroResult, anchorOnScreen: false };

    try {
      const orderedRowIds = rowsState.docIds
        .filter((id) => id !== AR_TABLE_AGGREGATIONS_ROW_ID) as ArRowId[];
      const anchorIndex = orderedRowIds.indexOf(anchorRowId);
      const nextRowId = anchorIndex >= 0 ? orderedRowIds[anchorIndex + 1] ?? null : null;

      const maxExistingOrder = Array.from(rowsDocs.values())
        .map((entry) => entry.data?.order)
        .filter((order): order is string => typeof order === 'string' && order.length > 0)
        .reduce((acc, order) => (acc === null || order > acc ? order : acc), null as string | null);

      const anchorOrder = rowsDocs.get(anchorRowId)?.data?.order;
      const nextNeighbourOrder = nextRowId ? rowsDocs.get(nextRowId)?.data?.order : undefined;

      const ranks = allocateRowOrders({
        floorOrder: typeof anchorOrder === 'string' && anchorOrder.length > 0
          ? anchorOrder
          : maxExistingOrder,
        ceilingOrder: typeof nextNeighbourOrder === 'string' && nextNeighbourOrder.length > 0
          ? nextNeighbourOrder
          : null,
        count: grid.length,
      });

      /**
       * `ArTableBody` owns the render plan, so this hook classifies the rows on
       * screen itself — the same classification the plan is built from — and asks
       * the same question of it. The membership rule stays in one place.
       */
      const anchorPosition = bundlePositionOf(
        classifyArBundleRows({ rowIds: rendered, rowsDocs }).items,
        anchorRowId,
      );
      const bundleFields = anchorPosition.kind === 'ungrouped'
        ? null
        : { bundleId: anchorPosition.bundleId, bundleRole: 'member' as const };

      /**
       * The payload each row lands with, minus the two timestamps: `persist` owns
       * `modifiedAt`, and `createdAt` goes out as a `serverTimestamp()` the client
       * cannot resolve here — the undo adapter reads the settled value off the row
       * on its way past instead.
       */
      const payloads = grid.map((row, index) => ({
        cells: Object.fromEntries(
          Object.entries(row).map(([typeId, value]) => [typeId, { value }])
        ) as Record<TypeId, ArCell>,
        order: ranks[index],
        ...bundleFields,
      }));

      const outcomes = await Promise.allSettled(payloads.map((payload) => persist.add({
        ...payload,
        createdAt: serverTimestamp(),
        modifiedAt: serverTimestamp(),
      })));

      /** Only rows that landed: a rejected add minted no id and created nothing to undo. */
      const landed = outcomes.flatMap((outcome, index) => (
        outcome.status === 'fulfilled'
          ? [{ docId: outcome.value as ArRowId, data: payloads[index] }]
          : []
      ));
      const newRowIds = landed.map((row) => row.docId);

      bumpPaginationCount(newRowIds.length);
      if (landed.length > 0) {
        formUndoStack.recordDocuments(persist.collectionRef.path, landed);
      }

      return {
        created: newRowIds.length,
        rejected: grid.length - newRowIds.length,
        newRowIds,
        anchorOnScreen: true,
        failed: false,
      };
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] paste rows', {
        userMessage: 'Failed to paste rows. Please try again.',
      });
      return { ...zeroResult, failed: true };
    }
  }, [bumpPaginationCount, handleFirestoreWriteError, persist, rowsDocs, rowsState.docIds, uiState]);

  /**
   * Locks or unlocks the scoped rows through the callable, which owns the
   * already-in-target-state skip the toast copy reports. Under `rowIds` the ids
   * are mirrored locally first, so the lock icons flip before the round-trip;
   * under `allRows` there is nothing to name, and the Firestore listener repaints.
   *
   * The mirror is reverted row by row when the call rejects (SYD-2386): no server
   * write followed it, so nothing would ever correct it, and `isRowLocked` feeds
   * `disabledOrLocked` in `ArCell` — a stale mirror leaves the rows read-only with
   * an error toast telling the user the lock failed. Reverting beats dropping the
   * mirror because the listener has no event to deliver on the reject path either.
   */
  const lockRows = useCallback(async (scope: ArTableRowScope, locked: boolean): Promise<BulkActionResult> => {
    const priorLockedById = new Map<ArRowId, boolean | undefined>();

    try {
      await persist.flushPending();

      for (const rowId of scope.rowIds ?? []) {
        const mutableRow = rowsState.docs.get(rowId);
        if (mutableRow?.data) {
          priorLockedById.set(rowId, mutableRow.data.locked);
          mutableRow.data.locked = locked;
        }
      }

      const { arTableRowOperations } = await import('@/services/arTableFunctions');
      const result = await arTableRowOperations({
        ...tableCoordinates,
        ...scope,
        operation: locked ? 'lock' : 'unlock',
      });

      return {
        affected: result.affectedCount,
        skipped: result.skippedCount,
        skippedIds: result.skippedIds as ArRowId[],
        failed: false,
      };
    } catch (error) {
      for (const [rowId, priorLocked] of priorLockedById) {
        const mutableRow = rowsState.docs.get(rowId);
        if (!mutableRow?.data) continue;
        if (priorLocked === undefined) {
          delete mutableRow.data.locked;
        } else {
          mutableRow.data.locked = priorLocked;
        }
      }

      handleFirestoreWriteError(error, '[useArTableActions] lock rows', {
        userMessage: isDeterministicRejection(error) && error instanceof Error
          ? error.message
          : `Failed to ${locked ? 'lock' : 'unlock'} rows. Please try again.`,
      });
      return scopeFallback(scope);
    }
  }, [persist, handleFirestoreWriteError, rowsState.docs, tableCoordinates]);

  /**
   * Deletes the scoped rows — every row delete in the table, down to one from the
   * right-click menu, because the trash the undo entry addresses is written by the
   * callable and by nothing else (SYD-2447). Locked rows and rows a Form Derive
   * projection owns are refused server-side, and under `allRows` the whole
   * collection is enumerated there rather than named here. The deterministic
   * rejections — an all-rows scope over `AR_TABLE_MAX_ROWS` — are shown verbatim;
   * retrying them changes nothing.
   *
   * A run that affected nothing records no entry: it has nothing to reverse, and an
   * entry for it would sit on the stack stealing the next ⌘Z. A half-committed run
   * records one anyway, from the rejection's own receipt — its rows are gone, and
   * the failure is still reported afterwards.
   *
   * That receipt moves the pagination counter too. Its rows left the collection as
   * surely as a clean run's did, and the undo the entry arms raises the counter by
   * the same number when it puts them back: leaving `totalRows` at its pre-delete
   * value would put the count 1,200 rows over the truth after a single ⌘Z, and
   * `totalPages` with it.
   */
  const deleteRows = useCallback(async (scope: ArTableRowScope): Promise<BulkActionResult> => {
    try {
      await persist.flushPending();

      const { arTableRowOperations } = await import('@/services/arTableFunctions');
      const result = await arTableRowOperations({
        ...tableCoordinates,
        ...scope,
        operation: 'delete',
      });

      bumpPaginationCount(-result.affectedCount);

      if (result.operationId !== undefined && result.affectedCount > 0) {
        formUndoStack.recordOperation(persist.collectionRef.path, {
          operationId: result.operationId,
          count: result.affectedCount,
          label: deletedRowsLabel(result.affectedCount),
        });
      }

      return {
        affected: result.affectedCount,
        skipped: result.skippedCount,
        skippedIds: result.skippedIds as ArRowId[],
        failed: false,
      };
    } catch (error) {
      const receipt = abortedDeleteReceipt(error);
      if (receipt !== null) {
        bumpPaginationCount(-receipt.count);
        formUndoStack.recordOperation(persist.collectionRef.path, receipt);
      }

      handleFirestoreWriteError(error, '[useArTableActions] delete rows', {
        userMessage: isDeterministicRejection(error) && error instanceof Error
          ? error.message
          : 'Failed to delete rows. Please try again.',
      });
      return scopeFallback(scope);
    }
  }, [persist, handleFirestoreWriteError, bumpPaginationCount, tableCoordinates]);

  /**
   * Reverses, or reapplies, one recorded delete. The rejection is deliberately not
   * caught: `formUndoStack.step` puts the entry back and rethrows, and
   * `useFormUndoKeybindings` owns the single report — swallowing it here would
   * leave a silent no-op behind an entry that looks spent.
   *
   * `entriesFound` gates the counter and travels on as `found`. An operation the
   * trash no longer holds — an id nothing was filed under, or one the TTL reaped —
   * restored nothing and holds nothing, so the count must not move for it whatever
   * else the response carries.
   *
   * A half-committed run moves the counter too, by the same reasoning the delete path
   * follows and with the direction deciding the sign: the batches that landed put
   * their rows back, or took them away, as surely as a clean run's would have. The
   * entry stays on the stack, because the target state does not hold yet, so the next
   * press moves only the remainder — a counter left where it was stays short by
   * everything the first press did, until a reload.
   */
  const stepRowOperation = useCallback(async (
    operationId: string,
    direction: UndoDirection,
  ): Promise<{ applied: number; skipped: number; found: number; targetStateHolds: boolean }> => {
    const { arTableRowUndo } = await import('@/services/arTableFunctions');
    try {
      const result = await arTableRowUndo({ ...tableCoordinates, operationId, direction });
      if (result.entriesFound > 0) {
        bumpPaginationCount(direction === 'undo' ? result.affectedCount : -result.affectedCount);
      }
      return {
        applied: result.affectedCount,
        skipped: result.skippedCount,
        found: result.entriesFound,
        targetStateHolds: result.targetStateHolds,
      };
    } catch (error) {
      const receipt = abortedRunReceipt(error);
      if (receipt !== null) {
        bumpPaginationCount(direction === 'undo' ? receipt.committedCount : -receipt.committedCount);
      }
      throw error;
    }
  }, [bumpPaginationCount, tableCoordinates]);

  /**
   * Duplicates multiple rows via Cloud Function (single round-trip).
   *
   * A bundle header in `rowIds` expands server-side into the whole bundle, so
   * `affected` counts documents created and can exceed `rowIds.length`. The two
   * deterministic callable rejections — the expanded write count exceeding
   * `AR_TABLE_PAGE_SIZE` and an exhausted order interval — carry their own reason
   * and are shown verbatim; retrying them changes nothing, so the generic
   * "please try again" wording is reserved for transport failures (SYD-2306).
   */
  const duplicateRows = useCallback(async (rowIds: ArRowId[]): Promise<BulkActionResult> => {
    try {
      await persist.flushPending();

      const { arTableRowOperations } = await import('@/services/arTableFunctions');
      const result = await arTableRowOperations({
        ...tableCoordinates,
        rowIds,
        operation: 'duplicate',
      });

      if (result.newRowIds.length > 0) {
        lastAddedRowId.current = result.newRowIds[result.newRowIds.length - 1];
      }

      bumpPaginationCount(result.affectedCount);

      return {
        affected: result.affectedCount,
        skipped: result.skippedCount,
        skippedIds: result.skippedIds as ArRowId[],
        failed: false,
      };
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] duplicate rows', {
        userMessage: isDeterministicRejection(error) && error instanceof Error
          ? error.message
          : 'Failed to duplicate rows. Please try again.',
      });
      return { affected: 0, skipped: rowIds.length, skippedIds: rowIds, failed: true };
    }
  }, [persist, handleFirestoreWriteError, bumpPaginationCount, tableCoordinates]);

  const moveRowInManualOrder = useCallback(async (nextRowIds: ArRowId[], rowId: ArRowId): Promise<void> => {
    try {
      const nextIndex = nextRowIds.indexOf(rowId);
      if (nextIndex === -1) {
        return;
      }

      const movingRowLocked = rowsState.docs.get(rowId)?.data?.locked === true;
      if (movingRowLocked) {
        return;
      }

      const prevId = nextIndex > 0 ? nextRowIds[nextIndex - 1] : null;
      const nextId = nextIndex < nextRowIds.length - 1 ? nextRowIds[nextIndex + 1] : null;

      const prevOrder = prevId
        ? rowsState.docs.get(prevId)?.data?.order
        : undefined;
      const nextOrder = nextId
        ? rowsState.docs.get(nextId)?.data?.order
        : undefined;

      const [candidateOrder] = allocateRowOrders({
        floorOrder: prevOrder && prevOrder.length > 0 ? prevOrder : null,
        ceilingOrder: nextOrder && nextOrder.length > 0 ? nextOrder : null,
        count: 1,
      });

      const movingEntry = rowsState.docs.get(rowId);
      if (movingEntry?.data) {
        movingEntry.data.order = candidateOrder;
      }
      await persist.update(rowId, { order: candidateOrder }, { debounce: false });
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] move row in manual order', {
        userMessage: 'Failed to reorder rows. Please try again.',
      });
    }
  }, [handleFirestoreWriteError, persist, rowsState.docs]);

  /**
   * Creates a bundle header above the topmost selected row and moves the selection
   * into it. Header and members are allocated in one `allocateRowOrders` call over
   * `(insertion point, first following row)` — the tightest interval, so no allocated
   * rank can equal an existing row's rank, not even transiently: the header lands
   * before its members are updated, and a member update that never lands must not
   * leave two rows sharing an `order` (SYD-2186). The interval is empty of other rows
   * by construction, since its bounds are adjacent in document order.
   */
  const createBundle = useCallback(async (
    selectedRowIds: ArRowId[],
    bundleType: ArBundleType,
    bundleValue?: ArBundleValue
  ): Promise<void> => {
    try {
      const resolvedBundleValue = bundleValue ?? (bundleType === 'group' ? 'New Group' : undefined);

      const hasRowsInExistingBundle = selectedRowIds.some(
        (id) => !!rowsDocs.get(id)?.data?.bundleId
      );

      if (hasRowsInExistingBundle) {
        const confirmed = await new Promise<boolean>((resolve) => {
          modal.confirm({
            title: 'Move rows to new group?',
            content: 'Some selected rows already belong to a group. They will be moved to the new group.',
            okText: 'Move',
            cancelText: 'Cancel',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!confirmed) return;
      }

      const orderedRowIds = (rowsState.docIds as ArRowId[]).filter(
        (id) => id !== AR_TABLE_AGGREGATIONS_ROW_ID
      );

      const firstSelectedIdx = selectedRowIds.reduce((min, id) => {
        const idx = orderedRowIds.indexOf(id);
        return idx >= 0 && (min === -1 || idx < min) ? idx : min;
      }, -1);

      const insertAfterIdx = (() => {
        if (firstSelectedIdx < 0) return orderedRowIds.length - 1;
        let idx = firstSelectedIdx - 1;
        while (idx >= 0) {
          const rowData = rowsDocs.get(orderedRowIds[idx])?.data;
          if (!rowData?.bundleRole) break;
          if (rowData.bundleRole === 'member') {
            const bundleHeaderId = rowData.bundleId as ArRowId | undefined;
            if (!bundleHeaderId) break;
            const headerIdx = orderedRowIds.indexOf(bundleHeaderId);
            if (headerIdx < 0) break;
            idx = headerIdx - 1;
          } else {
            idx--;
          }
        }
        return idx;
      })();

      const floorOrder = insertAfterIdx >= 0
        ? rowsDocs.get(orderedRowIds[insertAfterIdx])?.data?.order ?? null
        : null;

      const ceilingOrder = (() => {
        for (let i = insertAfterIdx + 1; i < orderedRowIds.length; i++) {
          const order = rowsDocs.get(orderedRowIds[i])?.data?.order;
          if (typeof order === 'string' && order.length > 0) return order;
        }
        return null;
      })();

      const memberRowIds = selectedRowIds;
      const orders = allocateRowOrders({
        floorOrder,
        ceilingOrder,
        count: memberRowIds.length + 1,
      });

      const newRowId = await addRow(undefined, { order: orders[0] });
      if (!newRowId) throw new Error('Failed to create bundle header row');
      const headerRowId = newRowId as ArRowId;

      const headerEntry = rowsDocs.get(headerRowId);
      if (headerEntry?.data) {
        headerEntry.data.bundleId = headerRowId;
        headerEntry.data.bundleRole = 'header';
        headerEntry.data.bundleType = bundleType;
        if (resolvedBundleValue !== undefined) headerEntry.data.bundleValue = resolvedBundleValue;
      }

      const headerUpdates: Record<string, unknown> = {
        bundleId: headerRowId,
        bundleRole: 'header',
        bundleType,
      };
      if (resolvedBundleValue !== undefined) headerUpdates.bundleValue = resolvedBundleValue;
      await persist.update(headerRowId, headerUpdates, { debounce: false });

      const memberWrites: Promise<void>[] = [];
      for (let i = 0; i < memberRowIds.length; i++) {
        const memberId = memberRowIds[i];
        const memberEntry = rowsDocs.get(memberId);
        const memberUpdates: Record<string, unknown> = {
          bundleId: headerRowId,
          bundleRole: 'member',
          order: orders[i + 1],
        };
        if (memberEntry?.data) {
          memberEntry.data.bundleId = headerRowId;
          memberEntry.data.bundleRole = 'member';
          memberEntry.data.order = orders[i + 1];
        }
        memberWrites.push(persist.update(memberId, memberUpdates, { debounce: false }));
      }
      await Promise.all(memberWrites);
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] create bundle', {
        userMessage: 'Failed to create bundle. Please try again.',
      });
    }
  }, [addRow, handleFirestoreWriteError, modal, persist, rowsDocs, rowsState.docIds]);

  /**
   * Ungroups a whole bundle through the callable, which strips the bundle fields
   * from the header and from every member the collection holds — not just the ones
   * this page paints. Nothing is written optimistically: a bundle can straddle a
   * pagination boundary, so a client-side dissolve would free the rows on screen
   * and leave the rest pointing at a header that is no longer one (SYD-2430).
   *
   * `affected` is what the server actually rewrote, which is the only count either
   * side can quote once the page stops bounding the bundle. Deterministic
   * rejections — a bundle over the row bound, a half-committed batch naming how
   * far it got — are shown verbatim: retrying changes nothing, or changes what the
   * message already said (SYD-2306).
   */
  const dissolveBundle = useCallback(async (headerRowId: ArRowId): Promise<BulkActionResult> => {
    try {
      await persist.flushPending();

      const { arTableRowOperations } = await import('@/services/arTableFunctions');
      const result = await arTableRowOperations({
        ...tableCoordinates,
        bundleId: headerRowId,
        operation: 'dissolve',
      });

      return {
        affected: result.affectedCount,
        skipped: result.skippedCount,
        skippedIds: result.skippedIds as ArRowId[],
        failed: false,
      };
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] dissolve bundle', {
        userMessage: isDeterministicRejection(error) && error instanceof Error
          ? error.message
          : 'Failed to dissolve bundle. Please try again.',
      });
      return scopeFallback({ bundleId: headerRowId });
    }
  }, [handleFirestoreWriteError, persist, tableCoordinates]);

  const removeFromBundle = useCallback(async (rowId: ArRowId): Promise<void> => {
    try {
      const entry = rowsDocs.get(rowId);
      if (entry?.data) {
        delete entry.data.bundleId;
        delete entry.data.bundleRole;
      }

      await persist.update(rowId, {
        bundleId: deleteField(),
        bundleRole: deleteField(),
      }, { debounce: false });
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] remove from bundle', {
        userMessage: 'Failed to remove row from bundle. Please try again.',
      });
    }
  }, [handleFirestoreWriteError, persist, rowsDocs]);

  /**
   * Joins `rowId` to `targetBundleId` and ranks it against the first anchor that
   * admits a sound rank: the aimed drop position, then the bundle's last on-page
   * member, then the header. The chain reconsiders rather than short-circuits —
   * a bundle can span a cursor page, so the aimed anchor is regularly a header
   * with no `order` on this page, and stopping there left the row a member that
   * never moved (SYD-2323). Only the header forbids being preceded. With no
   * sound rank anywhere the join lands without an `order` write.
   */
  const moveToBundle = useCallback(async (rowId: ArRowId, targetBundleId: string, afterRowId?: ArRowId): Promise<void> => {
    try {
      const entry = rowsDocs.get(rowId);
      if (entry?.data) {
        entry.data.bundleId = targetBundleId;
        entry.data.bundleRole = 'member';
      }

      const updates: Record<string, unknown> = {
        bundleId: targetBundleId,
        bundleRole: 'member',
      };

      const orderedRowIds = rowsState.docIds
        .filter((id) => id !== AR_TABLE_AGGREGATIONS_ROW_ID) as ArRowId[];

      const lastMemberId = (() => {
        let last: ArRowId | null = null;
        for (const id of orderedRowIds) {
          if (id === rowId) continue;
          const doc = rowsDocs.get(id);
          if (doc?.data?.bundleId === targetBundleId && doc.data.bundleRole === 'member') {
            last = id as ArRowId;
          }
        }
        return last;
      })();

      const anchors = [
        ...(afterRowId ? [{ rowId: afterRowId, mayPrecede: afterRowId !== targetBundleId }] : []),
        ...(lastMemberId ? [{ rowId: lastMemberId, mayPrecede: true }] : []),
        { rowId: targetBundleId, mayPrecede: false },
      ].filter((anchor, index, all) => all.findIndex((other) => other.rowId === anchor.rowId) === index);

      const nextRank = allocateMoveToBundleOrder({
        anchors,
        orderedRowIds,
        getOrder: (id) => rowsDocs.get(id)?.data?.order,
        pageDocCount: rowsState.docIds.length,
        pageSize: uiState.pagination.pageSize,
        currentPage: uiState.pagination.currentPage,
        totalPages: uiState.pagination.totalPages,
      });

      if (nextRank !== null) {
        updates.order = nextRank;
        if (entry?.data) {
          entry.data.order = nextRank;
        }
      }

      await persist.update(rowId, updates, { debounce: false });
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] move to bundle', {
        userMessage: 'Failed to move row to bundle. Please try again.',
      });
    }
  }, [handleFirestoreWriteError, persist, rowsDocs, rowsState.docIds, uiState]);

  const addMemberRow = useCallback(async (bundleId: string, afterRowId?: ArRowId): Promise<string | null> => {
    return addRow(undefined, { afterRowId, bundleId });
  }, [addRow]);

  /**
   * Copies a whole bundle — header plus every member row. The member list is
   * resolved server-side from the whole collection, not from the page on screen,
   * so a bundle spanning pages is copied entire; `affected` is therefore documents
   * created (1 + members) and may exceed the header's on-screen member count.
   */
  const duplicateBundle = useCallback(async (headerRowId: ArRowId): Promise<BulkActionResult> => {
    return duplicateRows([headerRowId]);
  }, [duplicateRows]);

  /**
   * Deletes a whole bundle. The scope is the bundle's id, never an enumerated list:
   * the server expands it into the header plus every member the collection holds,
   * so a bundle straddling a pagination boundary cannot leave headerless members
   * behind (SYD-2430). The counts pass straight through — the caller reports what
   * the server touched, and `deleteRows` owns every failure, including its own
   * toast, so there is nothing here to catch.
   */
  const deleteBundle = useCallback((headerRowId: ArRowId): Promise<BulkActionResult> => (
    deleteRows({ bundleId: headerRowId })
  ), [deleteRows]);

  /**
   * Re-ranks a bundle's members under the header it just moved. A locked member
   * is skipped, matching `moveRowInManualOrder`'s own refusal to re-rank a locked
   * row: the header's grip is live while a member is not, so a bundle move must
   * not become the way a locked row gets re-ordered. Ranks are allocated for the
   * unlocked members alone and handed out in their existing relative order, so
   * the survivors stay contiguous under the header rather than inheriting a hole
   * a skipped member left.
   *
   * The members come from a one-shot query over the whole collection, not from the
   * render plan: a bundle can straddle a pagination boundary, and an off-page member
   * left at its old rank detaches from the header that just moved (SYD-2430). The
   * query carries no `orderBy` — ArTable rows have no composite index — so the rows
   * are ordered here by the LexoRanks they came back with.
   */
  const moveBundleInManualOrder = useCallback(async (
    nextRowIds: ArRowId[],
    headerRowId: ArRowId
  ): Promise<void> => {
    try {
      await moveRowInManualOrder(nextRowIds, headerRowId);

      const headerOrder = rowsState.docs.get(headerRowId)?.data?.order;
      if (typeof headerOrder !== 'string') return;

      /**
       * Every candidate ceiling's rank is read here, before the round trip: a page
       * turn during the query swaps the page store's documents out, and a ceiling
       * that misses makes `allocateRowOrders` append past the bundle instead of
       * bisecting under it.
       */
      const headerIndex = nextRowIds.indexOf(headerRowId);
      const followingOrders = nextRowIds.slice(headerIndex + 1).map((rowId) => ({
        rowId,
        order: rowsState.docs.get(rowId)?.data?.order,
      }));

      const bundleSnapshot = await getDocs(
        query(persist.collectionRef, where('bundleId', '==', headerRowId)),
      );
      const members = bundleSnapshot.docs
        .map((memberDoc) => ({ rowId: memberDoc.id as ArRowId, data: memberDoc.data() as ArRowData }))
        .filter((member) => member.data.bundleRole === 'member')
        .sort((a, b) => {
          const left = a.data.order;
          const right = b.data.order;
          if (left === right) return 0;
          if (left === undefined) return 1;
          if (right === undefined) return -1;
          return left < right ? -1 : 1;
        });

      const movableMemberIds = members
        .filter((member) => member.data.locked !== true)
        .map((member) => member.rowId);
      if (movableMemberIds.length === 0) return;

      const memberIds = new Set(members.map((member) => member.rowId));
      const ceilingOrder = followingOrders
        .find((candidate) => !memberIds.has(candidate.rowId))?.order ?? null;

      const ranks = allocateRowOrders({
        floorOrder: headerOrder,
        ceilingOrder,
        count: movableMemberIds.length,
      });

      const memberWrites: Promise<void>[] = [];
      for (let i = 0; i < movableMemberIds.length; i++) {
        const memberId = movableMemberIds[i];
        const memberEntry = rowsState.docs.get(memberId);
        if (memberEntry?.data) {
          memberEntry.data.order = ranks[i];
        }
        memberWrites.push(persist.update(memberId, { order: ranks[i] }, { debounce: false }));
      }
      await Promise.all(memberWrites);
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] move bundle in manual order', {
        userMessage: 'Failed to move bundle. Please try again.',
      });
    }
  }, [handleFirestoreWriteError, moveRowInManualOrder, persist, rowsState.docs]);

  /**
   * Renames a bundle header. Unlike `annotation`, a first-time set is safe to
   * record: every reader goes through `resolveBundleLabel`, which tests
   * `typeof === 'string'` then truthiness, so a reverted `null` resolves to the
   * same 'Kit'/'Group' fallback an absent field does — the header is left in a
   * state the user can reach again by renaming.
   */
  const updateBundleValue = useCallback(async (headerRowId: ArRowId, bundleValue: ArBundleValue): Promise<void> => {
    try {
      const entry = rowsDocs.get(headerRowId);
      formUndoStack.record(persist.collectionRef.path, [{
        docId: headerRowId,
        path: 'bundleValue',
        prev: entry?.data?.bundleValue ?? null,
        next: bundleValue,
      }]);
      if (entry?.data) {
        entry.data.bundleValue = bundleValue;
      }
      await persist.update(headerRowId, { bundleValue } as Record<string, unknown>, { debounce: false });
    } catch (error) {
      handleFirestoreWriteError(error, '[useArTableActions] update bundle value', {
        userMessage: 'Failed to update bundle name. Please try again.',
      });
    }
  }, [handleFirestoreWriteError, persist, rowsDocs]);

  return useMemo(() => {
    return {
      addRow,
      clearRow,
      duplicateRow,
      lockRow,
      moveRowInManualOrder,
      updateAnnotation,
      getRowLocked,
      getRowAnnotation,
      lastAddedRowId,
      bumpPaginationCount,
      pasteDownColumn,
      pasteRows,
      lockRows,
      deleteRows,
      stepRowOperation,
      duplicateRows,
      createBundle,
      dissolveBundle,
      removeFromBundle,
      moveToBundle,
      addMemberRow,
      duplicateBundle,
      deleteBundle,
      moveBundleInManualOrder,
      updateBundleValue,
    };
  }, [
    addRow,
    clearRow,
    duplicateRow,
    lockRow,
    moveRowInManualOrder,
    updateAnnotation,
    getRowLocked,
    getRowAnnotation,
    lastAddedRowId,
    bumpPaginationCount,
    pasteDownColumn,
    pasteRows,
    lockRows,
    deleteRows,
    stepRowOperation,
    duplicateRows,
    createBundle,
    dissolveBundle,
    removeFromBundle,
    moveToBundle,
    addMemberRow,
    duplicateBundle,
    deleteBundle,
    moveBundleInManualOrder,
    updateBundleValue,
  ]);
}

export { useArTableActions };
export type { Actions, BulkActionResult, PasteResult, PastedRow, PasteRowsResult };
